import { coordEach } from '@turf/turf';
import { Feature } from 'ol';
import Map from 'ol/Map';
import { Coordinate } from 'ol/coordinate';
import { Geometry, Point, Polygon } from 'ol/geom';
import { DragPan } from 'ol/interaction';
import { InteractionOptions } from 'ol/interaction/Interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Circle, Fill, Stroke, Style } from 'ol/style';

import MapController from '@/shared/lib/openlayers/MapController';
import {
  AbstractInteraction,
  InteractionExtraOptions,
} from '@/shared/lib/openlayers/interaction/AbstractInteraction';

export class AbstractVerticeInteraction extends AbstractInteraction {
  static STYLE = new Style({
    fill: new Fill({ color: 'rgba(216,8,15,0.2)' }),
    stroke: new Stroke({ color: 'rgba(216,8,15,0.5)', width: 3, lineDash: [1, 5] }),
    image: new Circle({ radius: 5, fill: new Fill({ color: '#d8080f' }) }),
  });

  protected overlayLayer: VectorLayer;
  protected overlaySource: VectorSource;

  protected featureOverlay: Feature<Polygon>;

  protected handles: Feature<Point>[] = [];

  protected dragPanInteraction?: DragPan;
  protected dragging = false;
  protected draggingFeature?: Feature<Point> = undefined;
  protected draggingPath?: number;
  protected isSpace = false;

  constructor(opts: InteractionOptions, mapController: MapController) {
    super(opts, mapController);

    // @ts-ignore
    if (opts.isSpace !== undefined) {
      // @ts-ignore
      this.isSpace = opts.isSpace;
    }

    this.overlaySource = new VectorSource({ useSpatialIndex: true, wrapX: false });
    this.overlayLayer = new VectorLayer({
      source: this.overlaySource,
      updateWhileInteracting: true,
      style: AbstractVerticeInteraction.STYLE,
    });
    this.featureOverlay = new Feature({ geometry: this.getSelectedGeometry().clone() });
    this.overlaySource.addFeature(this.featureOverlay);
  }

  static getExtraOpts(): InteractionExtraOptions {
    return { hasSnap: true, hasSelection: true, selectionRequired: true };
  }

  generateVertices() {
    this.handles.forEach((handle) => this.overlaySource.removeFeature(handle));
    this.handles = [];
    coordEach(this.asGeoJSON(this.featureOverlay), (currentCoord: number[]) => {
      const handle = new Feature({ geometry: new Point(currentCoord) });
      this.handles.push(handle);
      this.overlaySource.addFeature(handle);
    });
  }

  getSelectedMarker(position: Coordinate): Feature<Point> | undefined {
    const selectedMarkers = this.overlaySource
      .getFeaturesAtCoordinate(position)
      .filter((feature) => (feature.getGeometry() as Geometry).constructor === Point);

    if (selectedMarkers.length === 0) {
      return undefined;
    }

    return (
      this.isSpace && selectedMarkers.length === 2 ? selectedMarkers[1] : selectedMarkers[0]
    ) as Feature<Point>;
  }

  getSelectedPolygon(position: Coordinate): Feature<Polygon> | undefined {
    const selectedMarkers = this.overlaySource
      .getFeaturesAtCoordinate(position)
      .filter((feature) => (feature.getGeometry() as Geometry).constructor === Polygon);

    if (selectedMarkers.length === 0) {
      return undefined;
    }

    return (
      this.isSpace && selectedMarkers.length === 2 ? selectedMarkers[1] : selectedMarkers[0]
    ) as Feature<Polygon>;
  }

  setMap(map: Map | null) {
    super.setMap(map);
    this.overlayLayer.setMap(map);
    if (map) {
      this.dragPanInteraction = map
        .getInteractions()
        .getArray()
        .find((interaction) => interaction.constructor == DragPan) as DragPan | undefined;
    }
  }

  startDragging(feature?: Feature<Point>) {
    this.dragging = true;
    this.dragPanInteraction?.setActive(false);
    this.draggingFeature = feature;
  }

  stopDragging() {
    this.dragging = false;
    this.dragPanInteraction?.setActive(true);
    this.draggingFeature = undefined;
  }
}
